function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));
    const previousScrollY = window.scrollY;

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.classList.add('in-transition');

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    window.scrollTo({
      top: previousScrollY,
      // we need to set to instant to prevent the default behavior of scrolling that is applied in CSS
      behavior: 'instant',
    });

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

// Detect iOS 26 and add a class to the body
(function detectIOS26() {
  try {
    const ua = navigator.userAgent || '';
    // Check for various iOS 26 patterns
    if (
      /OS\s?26[._\s]?/i.test(ua) ||
      /iPhone\s?OS\s?26/i.test(ua) ||
      /CPU\s?(?:iPhone\s?)?OS\s?26/i.test(ua) ||
      /Version\/26\./i.test(ua)
    ) {
      const addClass = () => document.body.classList.add('ios-26');
      if (document.body) addClass();
      else document.addEventListener('DOMContentLoaded', addClass, { once: true });
    }
  } catch (_) {
    // no-op
  }
})();

document.querySelectorAll('[id^="Details-"] > summary').forEach((summary) => {
  if (summary.parentElement.getAttribute('is') === 'accordion-details') return;

  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

// Skip link focus management for WCAG 2.4.3 compliance
function initSkipLinks() {
  const skipToMainContent = document.getElementById('skip-to-main-content');
  if (skipToMainContent) {
    skipToMainContent.addEventListener('click', function (e) {
      e.preventDefault();
      const mainContent = document.getElementById('MainContent');
      if (mainContent) {
        // Scroll first, then focus to ensure element is in view
        mainContent.scrollIntoView({ behavior: 'instant', block: 'start' });
        // Use setTimeout to ensure focus happens after scroll
        setTimeout(function () {
          mainContent.focus({ preventScroll: true });
        }, 0);
      }
    });
  }

  const skipToChat = document.getElementById('skip-to-live-chat');
  if (skipToChat) {
    skipToChat.addEventListener('click', function (e) {
      e.preventDefault();

      // Try Gorgias chat button first
      const gorgiasButton = document.getElementById('gorgias-chat-button');
      if (gorgiasButton) {
        // Open the Gorgias chat widget
        if (typeof GorgiasChat !== 'undefined' && GorgiasChat.open) {
          GorgiasChat.open();
        } else {
          // If GorgiasChat API not ready, click the button
          gorgiasButton.click();
        }
        // Focus the chat button so keyboard users know where they are
        gorgiasButton.focus();
        return;
      }

      // Try ChannelTalk chat button
      const channelTalkButton = document.getElementById('channeltalk-chat-button');
      if (channelTalkButton) {
        // Open the ChannelTalk chat widget
        if (typeof ChannelIO !== 'undefined') {
          ChannelIO('showMessenger');
        } else {
          // If ChannelIO API not ready, click the button
          channelTalkButton.click();
        }
        // Focus the chat button so keyboard users know where they are
        channelTalkButton.focus();
      }
    });
  }
}

// Initialize skip links when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSkipLinks);
} else {
  initSkipLinks();
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('details:not([is="details-dropdown"]) > summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not([data-close-main-drawer])'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    // Handle main drawer close button
    const mainDrawerCloseButton = this.querySelector('[data-close-main-drawer]');
    if (mainDrawerCloseButton) {
      mainDrawerCloseButton.addEventListener('click', this.onMainDrawerCloseClick.bind(this));
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  removeClosestAnimation(target) {
    target.classList.remove('scroll-trigger', 'animate--fade-in');
  }

  removeAnimations = (element, maxDepth) => {
    let currentElement = element;
    let depth = 0;

    while (currentElement && depth < maxDepth) {
      currentElement.classList.remove('scroll-trigger', 'animate--fade-in');
      if (currentElement.closest('.scroll-trigger')) {
        this.removeAnimations(currentElement.closest('.scroll-trigger'), maxDepth);
      }
      currentElement = currentElement.closest('.scroll-trigger');
      depth++;
    }
  };

  onSummaryClick(event) {
    const maxSearchDepth = 2;
    const targetElement = event.target.closest('.scroll-trigger.animate--fade-in');

    if (targetElement) {
      this.removeAnimations(targetElement, maxSearchDepth);
    }

    // Fire analytics event
    if (window.analytics) {
      // Get aria-label of initiating button
      const ariaLabel = event.currentTarget.getAttribute('aria-label') || event.currentTarget.textContent?.trim();
      window.analytics.track('Menu Drawer Opened', {
        url: window.location.pathname,
        title: ariaLabel || 'Unknown',
      });
    }

    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 1200px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      if (this.dataset.noSubmenu) {
        return;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    // Add a class to body when collection filter is open
    document.body.classList.add('collection-filter-open');
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details:not([is="accordion-details"])').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    // Remove the class when collection filter is closed
    document.body.classList.remove('collection-filter-open');

    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  onMainDrawerCloseClick(event) {
    event.preventDefault();
    const summaryElement = this.mainDetailsToggle.querySelector('summary');
    this.closeMenuDrawer(event, summaryElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  connectedCallback() {
    this.clickableRegions = this.querySelectorAll('.menu-drawer__menu-item');
    this.clickableRegions.forEach((region) => {
      region.addEventListener('touchstart', this.onRegionClick.bind(this), { passive: true });
    });
  }

  disconnectedCallback() {
    this.clickableRegions.forEach((region) => {
      region.removeEventListener('touchstart', this.onRegionClick.bind(this), { passive: true });
    });
  }

  async onRegionClick(event) {
    if (event.currentTarget) {
      const target = event.currentTarget;

      if (
        target.dataset.collectionHandle &&
        target.dataset.collectionTitle &&
        target.dataset.collectionUrl &&
        typeof window.writeCollectionData === 'function'
      ) {
        window.writeCollectionData(
          target.dataset.collectionHandle,
          target.dataset.collectionTitle,
          target.dataset.collectionUrl
        );
      }

      target.classList.add('touch-start');
      setTimeout(() => {
        target.classList.remove('touch-start');
      }, 300);
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');
    document.body.classList.add('mobile-menu--active');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    document.body.classList.remove('mobile-menu--active');
    document.body.classList.add('mobile-menu--closing');
    clearTimeout(this._menuCloseTimer);
    this._menuCloseTimer = setTimeout(() => document.body.classList.remove('mobile-menu--closing'), 700);

    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.HIDE_MODAL_CLOSE_TIMEOUT = Number(this.dataset.timeout) || 0;

    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));
    this.querySelectorAll('[data-modal-close]').forEach((button) => {
      button.addEventListener('click', this.hide.bind(this, false));
    });
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.classList.add('closing');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    setTimeout(() => {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      this.classList.remove('closing');
      this.dispatchEvent(new CustomEvent('modalClosed'));
    }, this.HIDE_MODAL_CLOSE_TIMEOUT);
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.defaultOptionValue = this.dataset.adjustDefaultOptionValueOnLoad || null;
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      // adjust logic so event is only fired from picker events since the entire main-product is wrapped within a variant-select
      if (event.target.closest('.product-variant-picker')) {
        this.updateSelectionMetadata(event);

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
            productId: this.dataset.productId,
          },
        });
      }
    });

    document.addEventListener('DOMContentLoaded', () => {
      if (!this.defaultOptionValue) return;

      const clearDefaultOptionState = () => {
        this.removeAttribute('data-adjust-default-option-value-on-load');
        const submitButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        if (submitButton) submitButton.disabled = false;
      };

      let optionValues;
      try {
        const parsed = JSON.parse(this.defaultOptionValue);
        optionValues = Array.isArray(parsed) ? parsed : [parsed];
      } catch (error) {
        optionValues = [this.defaultOptionValue];
      }

      let lastMatchedInput = null;
      optionValues.forEach((optionValue, index) => {
        const radioInput = this.querySelector(
          `input[type="radio"][name$="-${index + 1}"][value="${CSS.escape(optionValue)}"]`
        );
        if (radioInput) {
          radioInput.checked = true;
          lastMatchedInput = radioInput;
        }
      });

      if (lastMatchedInput) {
        document.addEventListener('product-info:loaded', clearDefaultOptionState, { once: true });
        setTimeout(clearDefaultOptionState, 3000);
        lastMatchedInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        clearDefaultOptionState();
      }
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const previouslySelected = Array.from(target.options).find((option) => option.getAttribute('selected'));
      if (previouslySelected) {
        previouslySelected.removeAttribute('selected');
      }
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) {
        const regex = /^(.*?)\s*\((.*?)\)\s*$/;
        const match = value.match(regex);
        if (selectedSwatchValue.dataset.splitTitle === 'true' && match) {
          const [, title, tag] = match;
          selectedSwatchValue.innerHTML = `${title.trim()} <span class="link__tag">${tag.trim()}</span>`;
        } else {
          selectedSwatchValue.innerHTML = value;
        }
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    // .external-selects relates to `external-swatches.liquid`
    // variant selects are detatched due to the re-ordering of color swatches for mobile
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
      .filter((element) => !element.closest('.in-transition'))
      .filter((element) => !element.closest('.external-selects'))
      .map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;
  idleCallbackId = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    // Stop any previous observers or idle callbacks
    this.observer?.unobserve(this);
    if (this.idleCallbackId) {
      cancelIdleCallback(this.idleCallbackId);
    }

    // Set up the IntersectionObserver
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        if (this.idleCallbackId) {
          cancelIdleCallback(this.idleCallbackId); // Cancel idle callback if observer triggers first
        }
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 800px 0px' }
    );
    this.observer.observe(this);

    // Set up the requestIdleCallback
    this.idleCallbackId = requestIdleCallback(() => {
      this.observer.unobserve(this); // Stop observing if idle callback triggers first
      this.loadRecommendations(productId);
    });
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('.shopify-section');
        if (recommendations?.innerHTML.trim().length) {
          this.outerHTML = recommendations.innerHTML;
          if (typeof window.initializeScrollAnimationTrigger === 'function') {
            window.initializeScrollAnimationTrigger();
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

formatMoney = function (cents, format) {
  if (typeof cents == 'string') {
    cents = cents.replace('.', '');
  }
  var value = '';
  var placeholderRegex = /{{\s*(\w+)\s*}}/;
  var formatString = format || Shopify.money_format;
  function defaultOption(opt, def) {
    return typeof opt == 'undefined' ? def : opt;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

window.formatMoney = formatMoney;

function scrambleTargets(productCard, scrambleAriaControls = false) {
  /*
    We need this to ensure product form options are unique, otherwise the browser will apply radio changes to other cards.
  */

  // Generate a random string to append to IDs and names
  const randomString = Math.random().toString(36).substring(2, 10);

  // Select all input elements within the product card
  const inputs = productCard.querySelectorAll('input.swatch-input__input');

  inputs.forEach((input) => {
    // Store the old ID before changing it
    const oldId = input.id;

    // Scramble the ID and name attributes
    const newId = `${oldId}-${randomString}`;
    const newName = `${input.name}-${randomString}`;

    // Update the input's ID and name attributes
    input.id = newId;
    input.name = newName;

    // Update the 'for' attribute of the corresponding label
    const label = productCard.querySelector(`label[for="${oldId}"]`);
    if (label) {
      label.htmlFor = newId;
    }
  });

  if (scrambleAriaControls) {
    const controlAttrs = ['aria-controls', 'data-modal-target'];

    for (const attr of controlAttrs) {
      const elements = Array.from(productCard.querySelectorAll(`[${attr}]`));
      const uniqueIds = [...new Set(elements.map((el) => el.getAttribute(attr)))];

      uniqueIds.forEach((oldId) => {
        const newId = `${oldId}-${randomString}`;
        productCard.querySelectorAll(`[${attr}="${oldId}"]`).forEach((el) => {
          el.setAttribute(attr, newId);
        });
        const targetElement = productCard.getElementById(oldId);
        if (targetElement) {
          targetElement.setAttribute('id', newId);
        }
        const dots = productCard.querySelectorAll(`[data-carousel-id="${oldId}"]`);
        if (dots.length > 0) {
          dots.forEach((dot) => {
            dot.setAttribute('data-carousel-id', newId);
          });
        }
      });
    }
  }
}

window.scrambleTargets = scrambleTargets;

function setOptionValues(target, productCard) {
  const index = target.closest(`.${target.wrapperTarget}`).querySelector('.option-value').dataset.targetOptionIndex;
  if (index !== '') {
    const isColor =
      target.closest(`.${target.wrapperTarget}`).querySelector('.option-value').dataset.isColor === 'true';

    const optionValues = productCard.querySelector(`script[data-json-type="current-options"]`).innerHTML;
    const optionValuesArray = JSON.parse(optionValues);
    const optionValue = optionValuesArray[index];

    productCard.querySelector(`.option-value`).setAttribute('data-target-option-index', index);
    productCard.querySelector(`.option-value`).setAttribute('data-is-color', isColor);

    if (!isColor) {
      productCard.querySelector(`.option-value`).innerHTML = optionValue;
      return;
    }

    const regex = /^(.*?)\s*\((.*?)\)\s*$/;
    const match = optionValue.match(regex);
    if (match) {
      const [, title, tag] = match;
      productCard.querySelector(`.option-value`).innerHTML =
        `${title.trim()} <span class="link__tag">${tag.trim()}</span>`;
    } else {
      productCard.querySelector(`.option-value`).innerHTML = optionValue;
    }
  }
}
window.setOptionValues = setOptionValues;

// dispatch concierge open from liquid rendered buttons
document.addEventListener('DOMContentLoaded', function () {
  function handleConciergeClick(event) {
    if (
      event.target.matches('concierge-trigger-button, [data-concierge-button], a[href*="?concierge"]') ||
      event.target.closest('concierge-trigger-button, [data-concierge-button], a[href*="?concierge"]')
    ) {
      event.preventDefault(); // Prevent default button behavior

      const target = event.target.matches('concierge-trigger-button, [data-concierge-button], a[href*="?concierge"]')
        ? event.target
        : event.target.closest('concierge-trigger-button, [data-concierge-button], a[href*="?concierge"]');

      let initialMessage = null;

      if (target.href) {
        const url = new URL(target.href);
        const conciergeParam = url.searchParams.get('concierge');
        if (conciergeParam) {
          initialMessage = decodeURIComponent(conciergeParam);
        }
      } else {
        initialMessage = target.dataset.conciergeInitialMessage || null;
      }

      const customEvent = new CustomEvent('concierge:open', {
        bubbles: true,
        detail: {
          trigger: target,
          timestamp: Date.now(),
          message: initialMessage,
          sectionId: target.dataset.sectionId || null,
        },
      });

      console.warn('Dispatching concierge:open event from', target);
      document.dispatchEvent(customEvent);
    }
  }

  // catch all concierge button clicks
  document.body.addEventListener('click', handleConciergeClick);
});

/**
 * Inject Review Summary block into third-party .oke-is-clickable element.
 * Clicking the block opens the hidden data-oke-reviews-summary content in a floating dialog.
 * The rest of .oke-is-clickable remains clickable and scrolls to reviews.
 */
(function injectOkeReviewSummary() {
  let activeBackdrop = null;
  let activeDialog = null;
  let closeOnClickOutside = null;

  const reviewSummaryBlock = () => {
    const wrapper = document.createElement('div');
    wrapper.className = 'oke-review-summary-block';
    wrapper.setAttribute('role', 'button');
    wrapper.setAttribute('tabindex', '0');
    wrapper.setAttribute('aria-expanded', 'false');
    wrapper.innerHTML =
      '<span class="oke-review-summary-separator">·</span> <span class="oke-review-summary-label">Review Summary</span>';
    return wrapper;
  };

  const closeDialog = () => {
    if (activeBackdrop) {
      activeBackdrop.remove();
      activeBackdrop = null;
    }
    if (activeDialog) {
      activeDialog.remove();
      activeDialog = null;
    }
    if (closeOnClickOutside) {
      document.removeEventListener('click', closeOnClickOutside);
      closeOnClickOutside = null;
    }
    document.removeEventListener('keydown', handleEscape);
    const openTrigger = document.querySelector('.oke-review-summary-block[aria-expanded="true"]');
    if (openTrigger) openTrigger.setAttribute('aria-expanded', 'false');
  };

  const handleEscape = (e) => {
    if (e.key === 'Escape') closeDialog();
  };

  const openDialog = (triggerEl) => {
    closeDialog();

    const wrapper = triggerEl.closest('.oke-review-summary-wrapper');
    if (!wrapper) return;

    const clickable = triggerEl.closest('.oke-is-clickable');
    const container = clickable ? clickable.parentElement : document.body;
    const summaryEl = container.querySelector ? container.querySelector('div[data-oke-reviews-summary]') : null;
    const source = summaryEl || document.querySelector('div[data-oke-reviews-summary]');
    if (!source) return;

    const backdrop = document.createElement('div');
    backdrop.className = 'oke-review-summary-dialog-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.addEventListener('click', closeDialog);

    const dialog = document.createElement('div');
    dialog.className = 'oke-review-summary-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-label', 'Review Summary');
    const content = source.cloneNode(true);
    content.style.display = '';
    content.removeAttribute('data-oke-reviews-summary');
    dialog.appendChild(content);

    const reviewsSummary = dialog.querySelector('.oke-reviewsSummary');
    if (reviewsSummary) {
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'oke-review-summary-dialog-close';
      closeBtn.setAttribute('aria-label', 'Close review summary');
      closeBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.7816 3.48586C11.9841 3.28336 12.3123 3.28336 12.5148 3.48586C12.7173 3.68835 12.7173 4.01658 12.5148 4.21908L4.21847 12.5154C4.01597 12.7179 3.68774 12.7179 3.48525 12.5154C3.28275 12.313 3.28275 11.9847 3.48525 11.7822L11.7816 3.48586Z" fill="#F3F3EF"/><path d="M3.48525 3.48586C3.68774 3.28336 4.01597 3.28336 4.21847 3.48586L12.5148 11.7822C12.7173 11.9847 12.7173 12.313 12.5148 12.5154C12.3123 12.7179 11.9841 12.7179 11.7816 12.5154L3.48525 4.21908C3.28275 4.01658 3.28275 3.68835 3.48525 3.48586Z" fill="#F3F3EF"/></svg>';
      closeBtn.addEventListener('click', closeDialog);
      reviewsSummary.insertBefore(closeBtn, reviewsSummary.firstChild);

      const footer = document.createElement('p');
      footer.className = 'oke-review-summary-dialog-footer';
      footer.textContent = 'Generated from customer reviews';
      const bodyText = reviewsSummary.querySelector('.oke-reviewsSummary-summary.oke-bodyText');
      if (bodyText && bodyText.nextSibling) {
        reviewsSummary.insertBefore(footer, bodyText.nextSibling);
      } else {
        reviewsSummary.appendChild(footer);
      }
    }

    document.body.appendChild(backdrop);
    wrapper.appendChild(dialog);
    wrapper.classList.add('oke-review-summary-wrapper--open');
    activeBackdrop = backdrop;
    activeDialog = dialog;
    activeWrapper = wrapper;
    triggerEl.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', handleEscape);

    closeOnClickOutside = (e) => {
      if (activeDialog && !activeDialog.contains(e.target) && !triggerEl.contains(e.target)) {
        closeDialog();
      }
    };
    setTimeout(() => document.addEventListener('click', closeOnClickOutside), 0);
  };

  const injectIntoClickable = (el) => {
    if (el.dataset.okeReviewSummaryInjected) return;
    const container = el.parentElement;
    const summaryRoot =
      container?.querySelector('div[data-oke-reviews-summary]') ??
      document.querySelector('div[data-oke-reviews-summary]');
    const bodyText = summaryRoot?.querySelector('.oke-reviewsSummary-summary.oke-bodyText');
    if (!bodyText || !bodyText.textContent.trim()) return;
    el.dataset.okeReviewSummaryInjected = 'true';
    const wrapperEl = document.createElement('div');
    wrapperEl.className = 'oke-review-summary-wrapper';
    const block = reviewSummaryBlock();
    block.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openDialog(block);
    });
    block.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        openDialog(block);
      }
    });
    wrapperEl.appendChild(block);
    el.insertAdjacentElement('afterend', wrapperEl);
  };

  const run = () => {
    document.querySelectorAll('.oke-is-clickable').forEach(injectIntoClickable);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  const observer = new MutationObserver(() => run());
  observer.observe(document.body, { childList: true, subtree: true });
})();
